import { useCallback, useRef, useEffect, useContext } from 'react';
import {
  generateShipmentOrder,
  generateInvoicePDF,
  sendClientOrder,
  sendProforma,
} from '../services/api';
import { extractErrorMessage, createErrorMessageBoxConfig } from '../utils/errorHandler';
import { PDFViewerContext } from '../components/PDFViewer/PDFViewerContext';

export const useDocumentActions = (
  formData,
  setMessageBoxConfig,
  setShowMessageBox,
  validateFields,
  setEmailSelectorFunction,
  setShowMailSelector
) => {
  const { openPDF } = useContext(PDFViewerContext);
  const formDataRef = useRef(formData);
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);
  const displayOrderPreview = useCallback(
    async e => {
      const fetchShipmentOrder = async formData => {
        try {
          const { blob, filename } = await generateShipmentOrder(formData);
          openPDF(blob, filename);
        } catch (error) {
          const errorConfig = createErrorMessageBoxConfig(error, () => setShowMessageBox(false));
          setMessageBoxConfig({
            ...errorConfig,
            message: `Une erreur est survenue lors de la génération du pdf: ${extractErrorMessage(
              error
            )}`,
          });
          setShowMessageBox(true);
        }
      };

      if (e) {
        e.preventDefault();
      }

      if (validateFields(['client', 'carrier', 'dates', 'addresses'])) {
        await fetchShipmentOrder(formDataRef.current);
      }
    },
    [setMessageBoxConfig, setShowMessageBox, validateFields, openPDF]
  );

  const handleClientOrderDisplay = useCallback(
    async e => {
      const fetchShipmentOrder = async formData => {
        try {
          const { blob, filename, response } = await generateShipmentOrder(
            formData,
            'client_order'
          );
          openPDF(blob, filename);
          return response;
        } catch (error) {
          const errorConfig = createErrorMessageBoxConfig(error, () => setShowMessageBox(false));
          setMessageBoxConfig({
            ...errorConfig,
            message: `Une erreur est survenue lors de la génération du pdf: ${extractErrorMessage(
              error
            )}`,
          });
          setShowMessageBox(true);
        }
      };

      if (e) {
        e.preventDefault();
      }

      if (validateFields(['client', 'carrier', 'dates', 'addresses'])) {
        const response = await fetchShipmentOrder(formDataRef.current);

        if (response) {
          setShowMessageBox(false);
          setEmailSelectorFunction(() => sendClientOrder);
          setShowMailSelector(true);
        }
      }
    },
    [
      setMessageBoxConfig,
      setShowMessageBox,
      validateFields,
      setEmailSelectorFunction,
      setShowMailSelector,
      openPDF,
    ]
  );

  const handleGenerateProforma = useCallback(async () => {
    const fetchProforma = async () => {
      try {
        const response = await generateInvoicePDF(
          formDataRef.current.client,
          [formDataRef.current],
          'proforma'
        );
        return response;
      } catch (error) {
        const errorConfig = createErrorMessageBoxConfig(error, () => setShowMessageBox(false));
        setMessageBoxConfig({
          ...errorConfig,
          message: `Une erreur est survenue lors de la génération de la proforma: ${extractErrorMessage(
            error
          )}`,
        });
        setShowMessageBox(true);
      }
    };

    if (validateFields(['client', 'dates', 'addresses'])) {
      const response = await fetchProforma();

      if (response) {
        setShowMessageBox(false);
        setEmailSelectorFunction(() => sendProforma);
        setShowMailSelector(true);
      }
    }
  }, [
    setMessageBoxConfig,
    setShowMessageBox,
    validateFields,
    setEmailSelectorFunction,
    setShowMailSelector,
  ]);

  const handleGenerateProformaClick = useCallback(async () => {
    setMessageBoxConfig({
      message: `Générer une proforma pour le client ${formDataRef.current.client} ?`,
      type: 'yesno',
      loading_button: true,
      input: false,
      onConfirm: handleGenerateProforma,
      onCancel: () => setShowMessageBox(false),
      onClose: () => setShowMessageBox(false),
    });
    setShowMessageBox(true);
  }, [handleGenerateProforma, setMessageBoxConfig, setShowMessageBox]);

  const handleConfirmEmailSelectorToSendClientFile = useCallback(
    async (client_emails, sendFunction) => {
      const SendDocument = async client_emails => {
        try {
          const response = await sendFunction(formDataRef.current, client_emails);
          return response;
        } catch (error) {
          const errorConfig = createErrorMessageBoxConfig(error, () => setShowMessageBox(false));
          setMessageBoxConfig({
            ...errorConfig,
            message: `Une erreur est survenue lors de l'envoi du document: ${extractErrorMessage(
              error
            )}`,
          });
          setShowMessageBox(true);
        }
      };

      const response = await SendDocument(client_emails);

      if (response) {
        setMessageBoxConfig({
          message: `Le document a bien été envoyé au client.`,
          type: 'ok',
          loading_button: false,
          input: false,
          onConfirm: () => setShowMessageBox(false),
          onClose: () => setShowMessageBox(false),
        });
        setShowMessageBox(true);
        setShowMailSelector(false);
      }
    },
    [setMessageBoxConfig, setShowMessageBox, setShowMailSelector]
  );

  return {
    displayOrderPreview,
    handleClientOrderDisplay,
    handleGenerateProforma,
    handleGenerateProformaClick,
    handleConfirmEmailSelectorToSendClientFile,
  };
};
